/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Switch, Route, Redirect } from 'react-router-dom';

import { homepage } from '../../../config';

import BackBar from '../../components/BackBar';

import GlobalHeader from '../GlobalHeader';
import Products from '../Products';
import Search from '../Search';
import InstanceDetails from '../InstanceDetails';
import Subscriptions from '../Subscriptions';
import Login from '../Login';
import LandingPage from '../LandingPage';
import MaintenanceModal from '../MaintenanceModal';
import ErrorComponent from '../../components/Error';
import SubscribeModal from '../SubscribeModal';
import GeneralMessages from '../GeneralMessages/index';
import GeneralMessageModal from '../GeneralMessageModal';
import GlobalFooter from '../../components/GlobalFooter';
import './base.css';

// V2 components
import { IncidentModal } from '../../../../components';

const BaseBody = styled.div`
  width: 100%;
  margin-top: ${(p) => {
    return p.top ? '0.75rem' : '0';
  }};
`;

const PushDown = styled.div`
  margin-top: ${(p) => {
    return p.marginTop ? p.marginTop + 'px' : 0;
  }};
`;

const SkipToMainContent = styled.a`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const BannerDiv = styled.div`
  height: 48px;
  position: fixed;
  top: 3rem;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  padding: 0 2rem;
  background-color: #056764;
  width: 100%;
  color: white;
  z-index: 4;

  p {
    flex: 1;
  }

  a {
    color: white;
  }
`

let isMounted = false;
class Base extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      hash: PropTypes.string.isRequired,
      key: PropTypes.string,
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string.isRequired,
      state: PropTypes.object
    }),
    history: PropTypes.shape({
      action: PropTypes.string
    })
  };

  constructor(props) {
    super(props);

    this.state = {
      previousLocation: props.location,
      showModal: false,
      bannerHeight: 0
    };
    this.handleResize = this.handleResize.bind(this);
  }

  handleResize(e) {
    if (isMounted) {
      const heightInPixels = 48;
      this.setState({ bannerHeight: heightInPixels });
    }
  }

  handleCloseBanner() {
    this.setState({ bannerHeight: 0 });
  }

  componentWillUnmount() {
    isMounted = false;
    window.removeEventListener('resize', this.handleResize);
  }

  componentDidMount() {
    isMounted = true;
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      if (this.props.globalError && this.props.globalError.notFound) {
        this.props.dispatch({ type: '@@globalError/CLEAR_NOT_FOUND' });
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { location: currentLocation } = this.props;
    const { location: nextLocation, history: nextHistory } = nextProps;

    // If Location change check to see if a modal was opened
    if (currentLocation !== nextLocation) {
      if (
        nextHistory.action !== 'POP' &&
        nextLocation.state &&
        nextLocation.state.modal
      ) {
        this.setState({ previousLocation: currentLocation, showModal: true });
      } else {
        this.setState({ previousLocation: undefined, showModal: false });
      }
    }
  }

  render() {
    if (this.props.globalError && this.props.globalError.notFound) {
      return <ErrorComponent errorCode={404} />;
    }

    // if any of the requests did not complete, throw an error,
    // so that the error boundary can render the error component
    Object.values(this.props.requests).forEach(request => {
      if (request.isErrored) {
        const error = new Error(request.error.rawResponse);
        error.status = request.error.status;

        // If the GET subscriber call fails, log the user out
        if (request.key === 'subscriber' && error.status === 403) {
          this.props.dispatch({ type: '@@token/Remove' });
        }

        // Only throw 5xx errors
        if (error.status >= 500) {
          throw error;
        }
      }
    })

    const bannerContent = homepage.banner;
    const { location } = this.props;
    const { previousLocation, showModal } = this.state;
    let switchLocation = showModal ? previousLocation : location;

    // if root location then default to /products/all/instances
    if (switchLocation.pathname === '/') {
      switchLocation = { ...switchLocation, pathname: '/current' };
    }
    const hasBackBar =
      switchLocation.pathname.includes('/alias/') ||
      switchLocation.pathname.includes('/instances/') ||
      switchLocation.pathname.includes('/search/') ||
      switchLocation.pathname.includes('/generalmessages/') ||
      switchLocation.pathname.includes('/subscriptions');
    const globalHeaderHeight = 48; // in pixels
    const backBarHeight = 40; // in pixels
    const backBarTop = this.state.bannerHeight + globalHeaderHeight;
    let pushDownMarginTop = backBarTop + 12;
    if (hasBackBar) {
      pushDownMarginTop += backBarHeight;
    }

    return (
      <div className="root-wrapper">
        <div className="main-container">
          <SkipToMainContent href="#maincontent">
            Skip to main content
          </SkipToMainContent>
          <GlobalHeader
            height={globalHeaderHeight}
            top={bannerContent ? this.state.bannerHeight : 0}
          />
          <BannerDiv>
            <p>{bannerContent}</p>
            <a href='https://my.trust.salesforce.com/'>Log In</a>
          </BannerDiv>
          {hasBackBar && (
            <BackBar
              height={backBarHeight}
              marginTop={this.state.bannerHeight + globalHeaderHeight}
            />
          )}
          <PushDown marginTop={pushDownMarginTop}>
            <BaseBody top={hasBackBar}>
              <Switch location={switchLocation}>
                <Route
                  path="/current"
                  render={(props) => {
                    return (
                      <LandingPage
                        {...props}
                        bannerHeight={this.state.bannerHeight}
                      />
                    );
                  }}
                />
                <Route path="/search/:instanceKey" component={Search} />
                <Route
                  path="/products/:product"
                  render={() => {
                    return <Products bannerHeight={this.state.bannerHeight} />;
                  }}
                />
                <Route
                  path="/instances/:instanceKey"
                  component={InstanceDetails}
                />
                <Route path="/alias/:aliasKey" component={InstanceDetails} />
                <Route path="/incidents/:id" component={IncidentModal} />
                <Route path="/maintenances/:id" component={MaintenanceModal} />
                <Route path="/subscriptions" component={Subscriptions} />
                <Route path="/subscribe/:aliasKey" component={SubscribeModal} />
                <Route
                  path="/generalmessages/:id"
                  component={GeneralMessageModal}
                />
                <Route path="/generalmessages" component={GeneralMessages} />
                <Route path="/login" component={Login} />
                <Redirect from="/status/incidents/:id" to="/incidents/:id" />
                <Redirect from="/status/maintenances/:id" to="/maintenances/:id" />
                <Route component={ErrorComponent} />
              </Switch>
              {showModal ? (
                <Route path="/incidents/:id" component={IncidentModal} />
              ) : null}
              {showModal ? (
                <Route path="/maintenances/:id" component={MaintenanceModal} />
              ) : null}
              {showModal ? (
                <Route path="/subscribe/:aliasKey" component={SubscribeModal} />
              ) : null}
              {showModal ? (
                <Route
                  path="/generalmessages/:id"
                  component={GeneralMessageModal}
                />
              ) : null}
            </BaseBody>
          </PushDown>
        </div>
        <GlobalFooter />
      </div>
    );
  }
}

export default Base;
